// The field capture screens' data and phone state: the projects and albums a photo can go to,
// whether there is signal, the stamp setting, the one location watch every screen shares, and
// the small "dismissed once" flags the hints keep.

import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useSyncExternalStore,
} from "react";
import { useLocation } from "@/hooks/use-location";
import { getJson } from "@/lib/api-client";
import { captureKeys } from "@/lib/query";
import { captureProjectsResponseSchema } from "@/schemas/uploads";

export function useCaptureProjects(enabled = true) {
  return useQuery({
    queryKey: captureKeys.projects(),
    queryFn: async () =>
      (await getJson("/api/capture/projects", captureProjectsResponseSchema)).data,
    enabled,
    // The list barely changes on site; the queue refreshes it after each upload.
    staleTime: 5 * 60_000,
  });
}

// ── Signal ──

function watchOnline(onChange: () => void) {
  window.addEventListener("online", onChange);
  window.addEventListener("offline", onChange);
  return () => {
    window.removeEventListener("online", onChange);
    window.removeEventListener("offline", onChange);
  };
}

const isOnline = () => navigator.onLine !== false;

export function useOnline(): boolean {
  return useSyncExternalStore(watchOnline, isOnline, () => true);
}

// ── Flags kept on this phone (localStorage; private mode keeps them for the visit) ──

const flagListeners = new Set<() => void>();
const memoryFlags = new Map<string, boolean>();

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return memoryFlags.get(key) ?? false;
  }
}

function writeFlag(key: string, value: boolean) {
  memoryFlags.set(key, value);
  try {
    if (value) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch {
    // Blocked storage: the memory copy holds it for this visit.
  }
  for (const listener of flagListeners) listener();
}

function watchFlags(onChange: () => void) {
  flagListeners.add(onChange);
  window.addEventListener("storage", onChange);
  return () => {
    flagListeners.delete(onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** A yes/no kept on this phone under `key`, false until set. */
export function usePhoneFlag(key: string): [boolean, (value: boolean) => void] {
  const value = useSyncExternalStore(
    watchFlags,
    () => readFlag(key),
    () => false,
  );
  const set = useCallback((next: boolean) => writeFlag(key, next), [key]);
  return [value, set];
}

const STAMP_KEY = "mesh-capture-stamp";

/** "Stamp date, time and location on photos": off until the crew turns it on. */
export function useStampSetting() {
  return usePhoneFlag(STAMP_KEY);
}

// ── Media queries ──

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

// ── One location watch for every capture screen ──

type CaptureLocation = ReturnType<typeof useLocation>;

const LocationContext = createContext<CaptureLocation | null>(null);

/**
 * Mounted by the capture layout, so moving between screens never restarts the watch and a
 * photo taken right after a tap still carries the last fix.
 */
export function CaptureLocationProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  return createElement(LocationContext.Provider, { value: location }, children);
}

export function useCaptureLocation(): CaptureLocation {
  const location = useContext(LocationContext);
  if (!location) throw new Error("useCaptureLocation must be used inside CaptureLocationProvider");
  return location;
}
